import Axios from '../../axiosConfig';
import { BACKEND_URL } from '../../config';
import { configure, runInAction, makeAutoObservable } from 'mobx';
import { EvaDefaultPerspectiveTemplateSetDto, OrganizationEvaTemplateDto } from '../../types/evaTemplate';

configure({ enforceActions: 'observed' });

export default class EvaTemplateModule {
    private organizationEvaTemplates: OrganizationEvaTemplateDto[] = [];
    private defaultEvaTemplates: EvaDefaultPerspectiveTemplateSetDto[] = [];

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    public async fetchOrganizationsEvaTemplates(): Promise<void> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + 'evaTemplate/getOrganizationsEvaTemplates',
            withCredentials: true,
        });
        runInAction(() => (this.organizationEvaTemplates = response.data));
    }

    public async fetchOrganizationEvaTemplates(id: number): Promise<void> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + `evaTemplate/getOrganizationEvaTemplates/${id}`,
            withCredentials: true,
        });
        runInAction(() => (this.organizationEvaTemplates = response.data));
    }

    public async fetchDefaultEvaTemplates(): Promise<void> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + 'evaTemplate/getDefaultEvaTemplates',
            withCredentials: true,
        });
        runInAction(() => (this.defaultEvaTemplates = response.data));
    }

    public async updateEvaTemplate(request: OrganizationEvaTemplateDto): Promise<void> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'evaTemplate/updateEvaTemplate',
            withCredentials: true,
            data: request,
        });
        const savedTemplate: OrganizationEvaTemplateDto = response.data;
        const index = this.organizationEvaTemplates.findIndex((template) => template.id === savedTemplate.id);
        if (index > -1) runInAction(() => (this.organizationEvaTemplates[index] = savedTemplate));
    }

    public async createEvaTemplate(request: OrganizationEvaTemplateDto): Promise<void> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'evaTemplate/createEvaTemplate',
            data: request,
            withCredentials: true,
        });
        const savedTemplate: OrganizationEvaTemplateDto = response.data;
        runInAction(() => this.organizationEvaTemplates.push(savedTemplate));
    }

    public async removeEvaTemplate(evaTemplateId: number): Promise<void> {
        await Axios({
            method: 'DELETE',
            url: BACKEND_URL + `evaTemplate/removeEvaTemplate/${evaTemplateId}`,
            withCredentials: true,
        });
        runInAction(() => {
            this.organizationEvaTemplates = this.organizationEvaTemplates.filter(
                (template) => template.id !== evaTemplateId,
            );
        });
    }

    public get organizationEvaTemplateList(): OrganizationEvaTemplateDto[] {
        return this.organizationEvaTemplates;
    }

    public get defaultEvaTemplateList(): EvaDefaultPerspectiveTemplateSetDto[] {
        return this.defaultEvaTemplates;
    }

    public getDefaultEvaTemplateById(id: number): EvaDefaultPerspectiveTemplateSetDto | null {
        const defaultEvaTemplate = this.defaultEvaTemplates.find((eva) => eva.id === id);
        return defaultEvaTemplate || null;
    }

    public getOrganizationEvaTemplateById(id: number): OrganizationEvaTemplateDto | null {
        const organizationEvaTemplate = this.organizationEvaTemplates.find((eva) => eva.id === id);
        return organizationEvaTemplate || null;
    }

    public getEvaTemplateById(id: number): OrganizationEvaTemplateDto | EvaDefaultPerspectiveTemplateSetDto | null {
        let template: OrganizationEvaTemplateDto | EvaDefaultPerspectiveTemplateSetDto | undefined;
        template = this.defaultEvaTemplates.find((eva) => eva.id === id);
        if (!template) template = this.organizationEvaTemplates.find((eva) => eva.id === id);

        return template || null;
    }
}
